import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { inr } from "../../lib/api";
import { Card, Loading, StatusBadge } from "../../components/admin/common";
import { ArrowLeft, User, CreditCard, History, GraduationCap } from "lucide-react";

const primaryOf = (ps) => (ps === "verified" ? "completed" : ps === "rejected" ? "rejected" : ps === "duplicate" ? "duplicate" : "pending");

function F({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</div>
      <div className={`text-slate-800 ${mono ? "font-mono text-sm" : ""}`}>{value || "—"}</div>
    </div>
  );
}

export default function StudentProfile() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get(`/students/${sid}/profile`).then((r) => setD(r.data)).catch(() => setD(false)); }, [sid]);

  if (d === null) return <Loading />;
  if (d === false) return <Card className="p-10 text-center text-slate-500">Student not found.</Card>;

  const s = d.student;
  const regs = d.registrations || [];
  const current = regs[0];
  const allPayments = regs.flatMap((r) => (r.payments || []).map((p) => ({ ...p, _reg: r.registration_id, _pid: r.payment_id })));
  const programNames = [...new Set(regs.map((r) => r.program_name).filter(Boolean))];
  const batchNames = [...new Set(regs.map((r) => `${r.program_name}|${r.level_name}|${r.batch_name}`).filter((x) => x.replace(/\|/g, "").trim()))];

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>

      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><GraduationCap className="w-6 h-6" /></div>
            <div>
              <h1 className="font-head text-2xl font-extrabold text-slate-900" data-testid="student-name">{s.student_name}</h1>
              <div className="text-xs text-slate-400 font-mono">Student ID: {(s.id || "").slice(0, 8).toUpperCase()}</div>
            </div>
          </div>
          {current && (
            <div className="text-right">
              <div className="text-sm text-slate-500">Current Batch: <span className="font-semibold text-slate-800">{current.level_name || "—"} · {current.batch_name || "—"}</span></div>
              <div className="mt-1"><StatusBadge status={current.payment_summary?.primary_status || primaryOf(current.payment_status)} /></div>
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-4 mb-6" data-testid="student-journey-summary">
        {[["Total Registrations", regs.length], ["Programs Enrolled", programNames.length], ["Batches Attended", batchNames.length]].map(([label, val]) => (
          <Card key={label} className="p-4 text-center">
            <div className="font-head text-2xl font-extrabold text-slate-900">{val}</div>
            <div className="text-xs text-slate-500 mt-0.5">{label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><User className="w-4 h-4 text-blue-600" /> Student Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <F label="Full Name" value={s.student_name} />
            <F label="Date of Birth" value={s.dob} />
            <F label="Gender" value={s.gender} />
            <F label="Standard / Class" value={s.standard} />
            <F label="Division" value={s.division} />
            <F label="Roll Number" value={s.roll_number} />
            <F label="Parent / Guardian" value={s.parent_name} />
            <F label="Mobile" value={s.mobile} mono />
            <F label="Email" value={s.email} />
          </div>
        </Card>

        {current && (
          <Card className="p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Current Enrollment</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <F label="School" value={current.school_name} />
              <F label="Program" value={current.program_name} />
              <F label="Level" value={current.level_name} />
              <F label="Batch" value={current.batch_name} />
              <F label="Registration ID" value={current.registration_id} mono />
              <F label="Registration Date" value={current.registration_date} />
              <F label="Required" value={inr(current.payment_summary?.required)} />
              <F label="Total Paid" value={inr(current.payment_summary?.total_paid)} />
              <F label="Remaining" value={inr(current.payment_summary?.remaining)} />
              <F label="Payment Status" value={<StatusBadge status={current.payment_summary?.primary_status} />} />
            </div>
          </Card>
        )}
      </div>

      <Card className="p-6 mb-6">
        <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><History className="w-4 h-4 text-blue-600" /> Batch & Program History <span className="text-xs font-normal text-slate-400">({regs.length} enrollment{regs.length !== 1 ? "s" : ""})</span></h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              {["Date", "School", "Program", "Level", "Batch", "Registration ID", "Paid / Required", "Status"].map((h) => <th key={h} className="py-2.5 pr-3 font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {regs.map((r) => (
                <tr key={r.registration_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)} data-testid="history-row">
                  <td className="py-2.5 pr-3 whitespace-nowrap">{r.registration_date}</td>
                  <td className="py-2.5 pr-3">{r.school_name || "—"}</td>
                  <td className="py-2.5 pr-3">{r.program_name || "—"}</td>
                  <td className="py-2.5 pr-3">{r.level_name || "—"}</td>
                  <td className="py-2.5 pr-3">{r.batch_name || "—"}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-blue-600">{r.registration_id}</td>
                  <td className="py-2.5 pr-3">{inr(r.payment_summary?.total_paid)} / {inr(r.payment_summary?.required)}</td>
                  <td className="py-2.5 pr-3"><StatusBadge status={r.payment_summary?.primary_status || primaryOf(r.payment_status)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Payment History <span className="text-xs font-normal text-slate-400">({allPayments.length} payment{allPayments.length !== 1 ? "s" : ""})</span></h3>
        {allPayments.length === 0 ? <p className="text-sm text-slate-400">No payments recorded yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {["Payment ID", "Registration ID", "Amount", "Transaction / UTR", "Date", "Method", "Status"].map((h) => <th key={h} className="py-2.5 pr-3 font-semibold whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {allPayments.map((p) => (
                  <tr key={p.payment_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/payments/${p.payment_id}`)} data-testid="payment-history-row">
                    <td className="py-2.5 pr-3 font-mono text-xs">{p._pid || p.payment_id}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-blue-600">{p._reg}</td>
                    <td className="py-2.5 pr-3 font-medium">{inr(p.detected_amount ?? p.paid_amount ?? 0)}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs">{p.utr || "—"}</td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{p.txn_date || "—"}</td>
                    <td className="py-2.5 pr-3">{p.payment_method || "—"}</td>
                    <td className="py-2.5 pr-3"><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
